<template>
  <b-modal
    title="ALTERAR SENHA"
    id="change_password"
    no-close-on-backdrop
    hide-footer
    @show="evOnShow"
    @shown="evOnShown"
    modal-class="change-password-modal"
  >
    <div class="password-change-container">
      <!-- Header Section -->
      <div class="modal-header-modern">
        <div class="header-icon">
          <i class="fas fa-key"></i>
        </div>
        <div class="header-content">
          <h3 class="header-title">Alterar Senha</h3>
          <p class="header-subtitle">
            Defina uma nova senha segura para sua conta
          </p>
        </div>
      </div>

      <!-- Content Section -->
      <div class="modal-content-modern">
        <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
          <!-- Security Tips -->
          <div class="security-tips">
            <div class="tips-header">
              <i class="fas fa-shield-alt mr-2"></i>
              <span>Dicas de Segurança</span>
            </div>
            <div class="tips-list">
              <div class="tip-item">
                <i class="fas fa-check-circle tip-icon"></i>
                <span>Use pelo menos 8 caracteres</span>
              </div>
              <div class="tip-item">
                <i class="fas fa-check-circle tip-icon"></i>
                <span>Combine letras, números e símbolos</span>
              </div>
              <div class="tip-item">
                <i class="fas fa-check-circle tip-icon"></i>
                <span>Evite informações pessoais</span>
              </div>
            </div>
          </div>

          <!-- Password Form -->
          <form @submit.prevent="evOnSubmit" class="password-form">
            <div class="form-section">
              <div class="input-group">
                <LabelRequiredField label="Nova Senha" class="modern-label" />
                <div class="password-input-wrapper">
                  <b-input-password
                    v-model="password"
                    ref="autofocus"
                    placeholder="Digite sua nova senha"
                  />
                </div>
              </div>

              <div class="input-group">
                <LabelRequiredField
                  label="Confirmar Nova Senha"
                  class="modern-label"
                />
                <div class="password-input-wrapper">
                  <b-input-password
                    v-model="confirm_password"
                    placeholder="Confirme sua nova senha"
                  />
                </div>
              </div>
            </div>

            <!-- Password Strength Indicator -->
            <div v-if="password" class="password-strength">
              <div class="strength-label">Força da senha:</div>
              <div class="strength-bar">
                <div
                  class="strength-fill"
                  :class="getPasswordStrengthClass()"
                  :style="{ width: getPasswordStrengthWidth() }"
                ></div>
              </div>
              <div class="strength-text" :class="getPasswordStrengthClass()">
                {{ getPasswordStrengthText() }}
              </div>
            </div>

            <!-- Required Fields Warning -->
            <div class="warning-section">
              <WarningRequiredFields v-if="permission('edit', 'user')" />
            </div>

            <!-- Error Messages -->
            <ErrorsMessages :errors="errors" />

            <!-- Action Buttons -->
            <div class="actions-section">
              <v-btn
                class="btn-cancel-modern"
                @click="$bvModal.hide('change_password')"
                :disabled="loading"
              >
                <i class="fas fa-times mr-2"></i>
                Cancelar
              </v-btn>
              <v-btn
                type="submit"
                class="btn-save-modern"
                :disabled="loading || !password || !confirm_password"
              >
                <i class="fas fa-save mr-2"></i>
                {{ loading ? "Salvando..." : "Salvar Nova Senha" }}
              </v-btn>
            </div>
          </form>
        </b-overlay>
      </div>
    </div>
  </b-modal>
</template>

<script>
import LabelRequiredField from "../Components/LabelRequiredField";
import WarningRequiredFields from "../Components/WarningRequiredFields";
import ErrorsMessages from "../Components/ErrorsMessages";
import permission from "../../lib/permission";
import toast from "../../lib/ToastNotification";
import api from "../../services/api";
import BInputPassword from "../Components/BInputPassword.vue";

export default {
  name: "ChangePassword",
  components: {
    LabelRequiredField,
    WarningRequiredFields,
    ErrorsMessages,
    BInputPassword,
  },
  props: {
    user_id: [Number, String],
    email: String,
  },
  data() {
    return {
      errors: [],
      loading: false,
      password: "",
      confirm_password: "",
    };
  },
  methods: {
    permission,
    async evOnShow() {
      this.errors = [];
      this.loading = false;
      this.password = "";
      this.confirm_password = "";
    },
    async evOnShown() {
      this.$refs.autofocus.focus();
    },
    async evOnSubmit() {
      try {
        this.loading = true;
        const params = {
          id: this.user_id,
          password: this.password,
          confirm_password: this.confirm_password,
        };
        const response = await api.patch(
          "/rest-home/user/change-password",
          params
        );
        const { error, errors, message } = response.data;
        if (error) {
          this.errors = errors;
        } else {
          this.$bvModal.hide("change_password");
          this.password = "";
          this.confirm_password = "";
          toast(message, "success");
          this.errors = [];
        }
        this.loading = false;
      } catch (error) {
        console.log(error);
        this.loading = false;
      }
    },
    getPasswordStrengthClass() {
      const strength = this.calculatePasswordStrength();
      if (strength < 30) return "strength-weak";
      if (strength < 60) return "strength-medium";
      if (strength < 80) return "strength-good";
      return "strength-strong";
    },
    getPasswordStrengthWidth() {
      return this.calculatePasswordStrength() + "%";
    },
    getPasswordStrengthText() {
      const strength = this.calculatePasswordStrength();
      if (strength < 30) return "Fraca";
      if (strength < 60) return "Média";
      if (strength < 80) return "Boa";
      return "Forte";
    },
    calculatePasswordStrength() {
      if (!this.password) return 0;
      let strength = 0;
      // Length check
      if (this.password.length >= 8) strength += 25;
      if (this.password.length >= 12) strength += 10;
      // Character variety
      if (/[a-z]/.test(this.password)) strength += 15;
      if (/[A-Z]/.test(this.password)) strength += 15;
      if (/[0-9]/.test(this.password)) strength += 15;
      if (/[^A-Za-z0-9]/.test(this.password)) strength += 20;
      return Math.min(strength, 100);
    },
  },
};
</script>

<style scoped>
/* Modal Customizations */
.change-password-modal >>> .modal-dialog {
  max-width: 520px;
}

.change-password-modal >>> .modal-content {
  border: none;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.change-password-modal >>> .modal-header {
  display: none;
}

/* Modal body override - força largura completa */
.change-password-modal >>> .modal-body {
  padding: 0 !important;
}

/* Container */
.password-change-container {
  padding: 0;
}

/* Header */
.modal-header-modern {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 32px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.header-subtitle {
  font-size: 14px;
  margin: 0;
  opacity: 0.9;
}

/* Content */
.modal-content-modern {
  padding: 32px 24px 24px;
  width: 100%;
}

.modal-content-modern form,
.modal-content-modern .form-section,
.modal-content-modern .input-group,
.modal-content-modern .password-input-wrapper {
  width: 100% !important;
  max-width: 100% !important;
}

.loading-overlay {
  border-radius: 0;
}

/* Form */
.password-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

/* Form Section - Garante largura total */
.modal-header-modern {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 32px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.header-subtitle {
  font-size: 14px;
  margin: 0;
  opacity: 0.9;
}

/* Content */
.modal-content-modern {
  padding: 32px 24px 24px;
}

.loading-overlay {
  border-radius: 0;
}

/* Security Tips */
.security-tips {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #7dd3fc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.tips-header {
  font-size: 14px;
  font-weight: 600;
  color: #0369a1;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.tips-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #0c4a6e;
}

.tip-icon {
  color: #10b981;
  font-size: 12px;
}

/* Form */
.password-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.modern-label {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
  width: 100%;
}

/* Password Inputs - Corrigido - Versão Final */
.password-input-wrapper {
  position: relative;
  width: 100% !important;
  display: block !important;
  min-width: 0 !important;
  max-width: 100% !important;
}

/* Força largura 100% em TODOS os elementos do input-group do bootstrap-vue */
.password-input-wrapper,
.password-input-wrapper >>> .b-input-password,
.password-input-wrapper >>> .input-group,
.password-input-wrapper >>> .input-group > * {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

.password-input-wrapper >>> .input-group {
  display: flex !important;
  align-items: stretch !important;
  flex-wrap: nowrap !important;
}

.password-input-wrapper >>> .input-group-prepend,
.password-input-wrapper >>> .input-group-append {
  display: flex !important;
  align-items: center !important;
  flex: 0 0 auto !important;
}

.password-input-wrapper >>> .form-control {
  flex: 1 1 auto !important;
  width: auto !important;
  min-width: 0 !important;
  border-radius: 12px 0 0 12px !important;
  border: 2px solid #e5e7eb !important;
  border-right: 1px solid #e5e7eb !important;
  padding: 12px 16px !important;
  font-size: 14px !important;
  background: #f9fafb !important;
  transition: all 0.3s ease !important;
  min-height: 44px !important;
  height: 44px !important;
  margin: 0 !important;
}

.password-input-wrapper >>> .form-control:focus {
  border-color: #f59e0b !important;
  background: white !important;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1) !important;
  outline: none !important;
  z-index: 3 !important;
}

.password-input-wrapper >>> .btn-outline-secondary {
  border: 2px solid #e5e7eb !important;
  border-left: 1px solid #e5e7eb !important;
  background: #f9fafb !important;
  color: #9ca3af !important;
  padding: 0 12px !important;
  border-radius: 0 12px 12px 0 !important;
  min-height: 44px !important;
  height: 44px !important;
  transition: all 0.3s ease !important;
  flex: 0 0 auto !important;
  width: auto !important;
  min-width: 44px !important;
}

.password-input-wrapper
  >>> .form-control:focus
  + .input-group-append
  .btn-outline-secondary,
.password-input-wrapper >>> .input-group:focus-within .btn-outline-secondary {
  border-color: #f59e0b !important;
  background: white !important;
}

.password-input-wrapper >>> .btn-outline-secondary:hover,
.password-input-wrapper >>> .btn-outline-secondary:focus {
  background: #f3f4f6 !important;
  color: #f59e0b !important;
  box-shadow: none !important;
  border-color: #e5e7eb !important;
}

/* Password Strength */
.password-strength {
  margin-top: 8px;
}

.strength-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
}

.strength-bar {
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 4px;
}

.strength-fill {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 3px;
}

.strength-weak {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
  color: #dc2626;
}

.strength-medium {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
  color: #d97706;
}

.strength-good {
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
  color: #2563eb;
}

.strength-strong {
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  color: #059669;
}

.strength-text {
  font-size: 12px;
  font-weight: 600;
  text-align: right;
}

/* Warning and Errors */
.warning-section {
  margin-top: 8px;
}

/* Actions */
.actions-section {
  display: flex;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
}

.btn-cancel-modern {
  flex: 1;
  background: transparent !important;
  color: #6b7280 !important;
  border: 2px solid #d1d5db !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 14px !important;
  box-shadow: none !important;
  transition: all 0.3s ease !important;
}

.btn-cancel-modern:hover:not(:disabled) {
  background: #f3f4f6 !important;
  border-color: #9ca3af !important;
  color: #374151 !important;
  transform: translateY(-1px) !important;
}

.btn-save-modern {
  flex: 1;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 14px !important;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-save-modern:hover:not(:disabled) {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4) !important;
}

.btn-save-modern:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* Responsive */
@media (max-width: 576px) {
  .change-password-modal >>> .modal-dialog {
    margin: 16px;
    max-width: none;
  }

  .modal-header-modern {
    padding: 24px 20px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .header-icon {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .header-title {
    font-size: 20px;
  }

  .modal-content-modern {
    padding: 24px 20px 20px;
  }

  .security-tips {
    padding: 16px;
  }

  .tips-list {
    gap: 6px;
  }

  .actions-section {
    flex-direction: column;
  }
}

@media (max-width: 400px) {
  .modal-header-modern {
    padding: 20px 16px;
  }

  .modal-content-modern {
    padding: 20px 16px 16px;
  }

  .header-title {
    font-size: 18px;
  }

  .header-subtitle {
    font-size: 13px;
  }
}
</style>